import type { StoryRefreshTriggerResponseApi } from "./storyRefreshNow.types";

/**
 * In-memory stand-in for `POST /workspaces/{id}/stories/{id}/refresh`, used
 * until the backend PR (doc-hub #46) merges. Promotion to live is an
 * environment-only change — see `storyRefreshNowApi.ts`.
 *
 * This mock reproduces the spec's three outcomes rather than always succeeding,
 * because the debounce/in-progress behaviour is the part of this endpoint most
 * likely to surprise the UI:
 *
 *   • inside DEBOUNCE_MS of the last trigger → 202 with the SAME job id (replay)
 *   • after that but still inside JOB_DURATION_MS → 409 "already in progress"
 *   • after JOB_DURATION_MS → 202 with a fresh job id
 *   • `force: true` → always a fresh job id, skipping both checks
 *
 * The 409 is thrown as a bare `Error` on purpose: the axios interceptor in
 * `services/api.ts` collapses real failures the same way, so call sites behave
 * identically against mock and live.
 */

/** Spec: repeat calls within 30 seconds are idempotent replays. */
export const DEBOUNCE_MS = 30_000;

/**
 * How long a mock refresh job pretends to run. Not from the spec — the backend
 * exposes no duration — chosen longer than DEBOUNCE_MS so the 409 window is
 * actually reachable in manual testing.
 */
export const JOB_DURATION_MS = 60_000;

interface MockJob {
  jobId: string;
  triggeredAtMs: number;
  triggeredAt: string;
}

const jobs = new Map<string, MockJob>();
let jobCounter = 0;

const key = (workspaceId: string, storyId: string): string =>
  `${workspaceId}:${storyId}`;

/**
 * Deterministic UUID-shaped ids. A real UUID would make assertions in
 * `storyRefreshNowApi.mock.test.ts` unwritable without stubbing crypto.
 */
const nextJobId = (): string => {
  jobCounter += 1;
  return `00000000-0000-4000-8000-${String(jobCounter).padStart(12, "0")}`;
};

const respond = (
  storyId: string,
  job: MockJob,
): StoryRefreshTriggerResponseApi => ({
  refresh_job_id: job.jobId,
  story_id: storyId,
  status: "queued",
  triggered_at: job.triggeredAt,
});

export const triggerMockStoryRefresh = async (
  workspaceId: string,
  storyId: string,
  force = false,
): Promise<StoryRefreshTriggerResponseApi> => {
  const now = Date.now();
  const existing = jobs.get(key(workspaceId, storyId));

  if (existing && !force) {
    const elapsed = now - existing.triggeredAtMs;

    if (elapsed < DEBOUNCE_MS) {
      return respond(storyId, existing);
    }

    if (elapsed < JOB_DURATION_MS) {
      throw new Error("A refresh is already in progress for this story.");
    }
  }

  const job: MockJob = {
    jobId: nextJobId(),
    triggeredAtMs: now,
    triggeredAt: new Date(now).toISOString(),
  };
  jobs.set(key(workspaceId, storyId), job);

  return respond(storyId, job);
};

/** Test-only cleanup for the module-scoped mock store. */
export const resetMockStoryRefresh = (): void => {
  jobs.clear();
  jobCounter = 0;
};
